import React, { useState, useEffect, useMemo } from "react";
import { string, bool, shape } from "prop-types";
import {
  loadScript,
  createPlayer,
  hasTargetingConsent,
} from "./youtubeService.js";

import uniqueid from "lodash.uniqueid";
import cx from "classnames";

const propTypes = {
  id: string.isRequired,
  title: string,
  poster: shape({
    alt: string,
    srcset: string,
    sizes: string,
  }),
  caption: string,
};

const YouTubeVideo = props => {
  const { id, title, poster, caption } = props;

  const [videoStarted, setVideoStarted] = useState(false);
  const [player, setPlayer] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);
  const [thumbnail, setThumbnail] = useState(
    poster || {
      alt: title,
      srcset: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
      sizes: "100vw",
    },
  );

  const isMP4 = id?.endsWith(".mp4") || id?.includes(".mp4");
  const playerId = useMemo(() => uniqueid(), []);

  const [consented, setConsented] = useState(hasTargetingConsent());

  useEffect(() => {
    const onConsentUpdate = () => setConsented(hasTargetingConsent());
    window.addEventListener("OneTrustGroupsUpdated", onConsentUpdate);
    return () =>
      window.removeEventListener("OneTrustGroupsUpdated", onConsentUpdate);
  }, []);

  useEffect(() => {
    // MP4s don't use YouTube; the YouTube player may only load with Targeting consent
    if (isMP4 || !consented) {
      return;
    }

    // On mount, check to see if the API script is already loaded
    loadScript();

    // Check if window.onYouTubeIframeAPIReady already exists
    if (window.YT) {
      const newPlayer = createPlayer(playerId, id, onPlayerReady);
      setPlayer(newPlayer);
    } else {
      // after onYouTubeIframeAPIReady is executed, create a new player
      const callback = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (callback) {
          callback();
        }

        const newPlayer = createPlayer(playerId, id, onPlayerReady);
        setPlayer(newPlayer);
        setThumbnail(
          poster || {
            alt: title,
            srcset: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
            sizes: "100vw",
          },
        );
      };
    }
  }, [id, playerId, consented]);

  useEffect(() => {
    // If video's started but player is not ready, play video when ready
    if (videoStarted && playerReady) {
      player.playVideo();
    }
  }, [player, playerReady, videoStarted]);

  const onPlayerReady = () => {
    setPlayerReady(true);
  };

  const playVideo = () => {
    setVideoStarted(true);

    if (playerReady) {
      player.playVideo();
    }
  };

  if (!isMP4 && !consented) {
    return (
      <div className="youtube-video youtube-video--consent-required">
        {title && <h2 className="youtube-video__title">{title}</h2>}
        <div className="youtube-video__consent-placeholder gdpr-info-box">
          <p className="youtube-video__consent-title">
            We&apos;re sorry, this content is unavailable.
          </p>
          <p className="youtube-video__consent-desc">
            To improve your experience, make sure Marketing Cookies are enabled.
          </p>
          <button
            className="btn btn-tertiary youtube-video__consent-button"
            onClick={() =>
              window.OneTrust && window.OneTrust.ToggleInfoDisplay()
            }
            type="button"
          >
            Update your cookie settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx("youtube-video", {
        "youtube-video--video-started": videoStarted,
      })}
    >
      {title && <h2 className="youtube-video__title">{title}</h2>}
      <figure className="youtube-video__media">
        <div className="youtube-video__video-wrapper">
          <button
            className={cx("youtube-video__poster-button", {})}
            onClick={playVideo}
            type="button"
          >
            {thumbnail && thumbnail.srcset ? (
              <img
                className="youtube-video__poster-image"
                alt={thumbnail.alt}
                srcSet={thumbnail.srcset}
                sizes={thumbnail.sizes || "(min-width: 1440px) 1440px, 100vw"}
              />
            ) : (
              <img
                className="youtube-video__poster-image"
                alt=""
                src="/-/media/thumbnails/feature/multimedia/youtubethumbnail.png"
              />
            )}
          </button>
          {isMP4 ? (
            videoStarted && (
              <video
                className="youtube-video__player"
                controls
                autoPlay
                poster={poster?.srcset}
              >
                <source src={id} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )
          ) : (
            <div id={playerId} />
          )}
        </div>
        <div className="youtube-video__caption">
          {caption && (
            <figcaption className="youtube-video__figure-caption">
              {caption && (
                <span className="youtube-video__description">{caption}</span>
              )}
            </figcaption>
          )}
        </div>
      </figure>
    </div>
  );
};

YouTubeVideo.propTypes = propTypes;

export default YouTubeVideo;
